import { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Copy, RefreshCw, Loader2, Eye, EyeOff, ShieldCheck, ShieldAlert, Globe, Clock, UserPlus, Trash2, Link2, AlertTriangle, CheckCircle2, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { PageHeader } from '@/components/PageHeader';
import { WebhookEndpoint, useConfigurationCopy } from '@/components/WebhookEndpoint';
import { Row } from '@/components/SettingsRow';
import { useTimezone, TIMEZONE_OPTIONS } from '@/components/TimezoneContext';
import { SurfaceHead } from '@/workspace/ui';
import { useMe } from '@/hooks/useMe';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const SECTIONS = [{ id: 'general', label: 'General' }, { id: 'tracking', label: 'Tracking' }, { id: 'team', label: 'Team' }, { id: 'developer', label: 'Developer' }, { id: 'billing', label: 'Subscription' }];
const FUNNEL_OPTIONS = [
  { value: 'webinar',   label: 'Webinar' },
  { value: 'vsl_long',  label: 'VSL · Long nurture' },
  { value: 'vsl_short', label: 'VSL · Short nurture' },
  { value: 'ecom',      label: 'E-commerce' },
  { value: 'standard',  label: 'Standard' },
];

function MonoRow({ value, masked, label }) {
  const { copy, copying, copyError } = useConfigurationCopy(value, label);
  const [shownValue, setShownValue] = useState(null);
  const show = !masked || shownValue === value;
  const display = show ? value : (value ? '•'.repeat(18) : '');
  return (
    <div className="sp-settings-inline flex items-center gap-2 rounded-md px-3 py-2"
      style={{ backgroundColor: 'var(--surface-2)', border: '1px solid var(--stroke-soft)' }}>
      <code className="text-[12px] flex-1 min-w-0 break-all" style={{ color: 'var(--ink)' }}>{display || '—'}</code>
      {masked && (
        <button type="button" disabled={!value} aria-pressed={show} aria-label={`${show ? 'Hide' : 'Show'} ${label}`} onClick={() => setShownValue(show ? null : value)} className="shrink-0 p-1 rounded hover:bg-[rgba(32,33,31,0.06)]" style={{ color: 'var(--text-dim)' }}>
          {show ? <EyeOff size={12} /> : <Eye size={12} />}
        </button>
      )}
      <button type="button" disabled={copying || !value} aria-busy={copying} aria-label={`Copy ${label}`} onClick={copy} className="shrink-0 p-1 rounded hover:bg-[rgba(32,33,31,0.06)]" style={{ color: 'var(--text-dim)' }}>
        <Copy size={12} />
      </button>
      {copyError && <p role="alert" className="sp-form-error w-full">{copyError}</p>}
    </div>
  );
}

const inputStyle = { backgroundColor: '#ffffff', border: '1px solid var(--stroke)', color: 'var(--text)', height: 36 };
const btnPrimary = { height: 36, backgroundColor: 'var(--ink)', color: '#ffffff', border: '1px solid var(--ink)' };
const btnGhost   = { height: 36, color: 'var(--text-muted)' };

function VerifyBadge({ verified, domain }) {
  if (!domain) return null;
  return verified ? (
    <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-md"
      style={{ backgroundColor: 'var(--lime-tint)', color: 'var(--brand-red)', border: '1px solid var(--lime-border)' }}>
      <ShieldCheck size={11} /> Verified
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-md"
      style={{ backgroundColor: '#faf4e7', color: '#a27a2c', border: '1px solid #eee0ba' }}>
      <ShieldAlert size={11} /> Not verified
    </span>
  );
}

export default function SettingsPage() {
  const [section, setSection] = useState('general');
  const qc = useQueryClient();
  const me = useMe();
  const { timezone, setTimezone } = useTimezone();
  const [company, setCompany] = useState('');
  const [domain, setDomain]   = useState('');
  const [subLabel, setSubLabel] = useState('');
  const [regTags, setRegTags] = useState('');
  const [saving, setSaving]   = useState('');
  const dirty = useRef({});
  const pending = useRef(false);
  const [error, setError] = useState('');
  const [verifyMsg, setVerifyMsg] = useState(null);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteLink, setInviteLink]   = useState(null);
  const [cancelRequested, setCancelRequested] = useState(null);
  const [rulePattern, setRulePattern] = useState('');
  const [ruleTag, setRuleTag] = useState('');

  const cancelDate = cancelRequested || me?.cancel_requested_at;

  const begin = which => {
    if (pending.current) return false;
    pending.current = true;
    setSaving(which);
    setError('');
    return true;
  };
  const finish = () => { pending.current = false; setSaving(''); };
  const reportError = e => {
    const message = e.message || 'Request failed. Please try again.';
    setError(message);
    toast.error(message);
  };

  const requestCancel = async () => {
    if (pending.current || cancelDate) return;
    if (!window.confirm('Request cancellation of your Tether subscription? Someone will reach out to confirm before anything changes.')) return;
    if (!begin('cancel')) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/tenant/cancel-subscription`, { method: 'POST' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Request failed');
      setCancelRequested(data.requested_at);
      qc.invalidateQueries({ queryKey: ['me'] });
      toast.success('Cancellation request sent');
    } catch (e) {
      reportError(e);
    } finally {
      finish();
    }
  };

  const teamQuery = useQuery({
    queryKey: ['team'],
    queryFn: () => fetch(`${BACKEND_URL}/api/tenant/users`).then(r => {
      if (!r.ok) throw new Error('Failed to load team');
      return r.json();
    }),
  });

  const team = teamQuery.data || [];

  const ruleQuery = useQuery({
    queryKey: ['tag-rules'],
    queryFn: () => fetch(`${BACKEND_URL}/api/tag-rules`).then(r => {
      if (!r.ok) throw new Error('Could not load tag rules');
      return r.json();
    }),
  });
  const rules = ruleQuery.data?.rules || [];
  const rulesUnavailable = ruleQuery.isError || ruleQuery.isLoading || ruleQuery.isPending;
  const { data: tagData } = useQuery({
    queryKey: ['tags'],
    queryFn: () => fetch(`${BACKEND_URL}/api/tags`).then(r => (r.ok ? r.json() : { tags: [] })),
  });
  const knownTags = tagData?.tags || [];

  const saveRules = async (next) => {
    if (rulesUnavailable || !begin('rules')) return false;
    try {
      const res = await fetch(`${BACKEND_URL}/api/tag-rules`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ rules: next }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Save failed');
      qc.setQueryData(['tag-rules'], data);
      await Promise.all([qc.invalidateQueries({ queryKey: ['tag-rules'] }), qc.invalidateQueries({ queryKey: ['tags'] })]);
      return true;
    } catch (e) {
      reportError(e);
      return false;
    } finally {
      finish();
    }
  };

  const addRule = async () => {
    if (!rulePattern.trim() || !ruleTag.trim()) { toast.error('Enter a page URL and a tag'); return; }
    if (await saveRules([...rules, { pattern: rulePattern.trim(), tag: ruleTag.trim() }])) {
      setRulePattern('');
      setRuleTag('');
      toast.success('Rule added');
    }
  };

  const removeRule = async (rule) => {
    if (pending.current || !window.confirm(`Remove the rule ${rule.pattern} → ${rule.tag}? Future visits will not receive this tag from the rule; existing contact tags will be kept.`)) return;
    if (await saveRules(rules.filter(r => r.id !== rule.id))) toast.success('Rule removed');
  };

  const sendInvite = async () => {
    if (pending.current) return;
    const email = inviteEmail.trim().toLowerCase();
    if (!email || !email.includes('@')) { toast.error('Enter a valid email'); return; }
    if (!begin('invite')) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/tenant/users/invite`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Invite failed');
      setInviteEmail('');
      qc.invalidateQueries({ queryKey: ['team'] });
      if (data.email_sent) {
        setInviteLink(null);
        toast.success(`Invite emailed to ${email}`);
      } else {
        toast.success('Invite created — share the link below (email not configured)');
        setInviteLink(data.invite_link);
      }
    } catch (e) {
      reportError(e);
    } finally {
      finish();
    }
  };

  const removeMember = async (u) => {
    if (pending.current || u.role === 'owner') return;
    if (!window.confirm(`Remove ${u.email} from the workspace?`)) return;
    if (!begin(`member-${u.id}`)) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/tenant/users/${u.id}`, { method: 'DELETE' });
      if (!res.ok && res.status !== 204) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || 'Remove failed');
      }
      qc.invalidateQueries({ queryKey: ['team'] });
      toast.success('Removed');
    } catch (e) {
      reportError(e);
    } finally {
      finish();
    }
  };

  useEffect(() => {
    if (me) {
      if (!dirty.current.company) setCompany(me.name || '');
      if (!dirty.current.domain) setDomain(me.tracking_domain || '');
      if (!dirty.current.subdomain) setSubLabel(me.tracking_subdomain || '');
      if (!dirty.current.regtags) setRegTags((me.registration_tags || []).join(', '));
    }
  }, [me]);

  const put = async (body, which) => {
    if (!begin(which)) return false;
    try {
      const res = await fetch(`${BACKEND_URL}/api/tenant/settings`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Save failed');
      dirty.current[which] = false;
      qc.setQueryData(['me'], current => ({ ...current, ...data }));
      await qc.invalidateQueries({ queryKey: ['me'] });
      toast.success('Saved');
      return true;
    } catch (e) {
      reportError(e);
      return false;
    } finally {
      finish();
    }
  };

  const saveSubdomain = () => {
    if (pending.current || !subLabel.trim() || subLabel.trim() === me?.tracking_subdomain) return;
    if (me?.tracking_subdomain && !window.confirm('Change your tracking subdomain? Snippets installed with the old hostname will stop working. Update those installations after saving.')) return;
    return put({ tracking_subdomain: subLabel.trim() }, 'subdomain');
  };

  const saveDomain = async () => {
    if (pending.current) return;
    if (!domain.trim() && me?.tracking_domain) return clearDomain();
    if (me?.domain_verified && domain.trim() !== me.tracking_domain && !window.confirm('Replace the live tracking domain? The new hostname must be verified before it is used. Existing installations may need updating.')) return;
    if (await put({ tracking_domain: domain.trim() }, 'domain')) setVerifyMsg(null);
  };

  const clearDomain = async () => {
    if (pending.current || !window.confirm('Clear the saved tracking domain? New snippets and webhook URLs will use the fallback domain. Existing installations may need updating.')) return;
    if (await put({ tracking_domain: '' }, 'domain')) {
      dirty.current.domain = true;
      setDomain('');
      setVerifyMsg(null);
    }
  };

  const verifyDomain = async () => {
    if (!me?.tracking_domain || domain.trim() !== me.tracking_domain) return;
    if (!begin('verify')) return;
    setVerifyMsg(null);
    try {
      const res = await fetch(`${BACKEND_URL}/api/tenant/tracking-domain/verify`, { method: 'POST' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Verification failed');
      setVerifyMsg(data);
      qc.invalidateQueries({ queryKey: ['me'] });
      if (data.verified) toast.success('Domain verified — tracking is live on it');
      else toast.error('Not verified yet — see the diagnosis below');
    } catch (e) {
      reportError(e);
    } finally {
      finish();
    }
  };

  const rotateKey = async () => {
    if (pending.current) return;
    if (!window.confirm('Rotate the API key? Webhooks using the current key will stop working immediately.')) return;
    if (!begin('key')) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/tenant/api-key/rotate`, { method: 'POST' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Rotate failed');
      qc.setQueryData(['me'], current => ({ ...current, api_key: data.api_key }));
      await qc.invalidateQueries({ queryKey: ['me'] });
      toast.success('New API key issued');
    } catch (e) {
      reportError(e);
    } finally {
      finish();
    }
  };

  const cnameTarget  = me?.cname_target || 'app.tethertracking.com';
  const domainLive   = !!(me?.tracking_domain && me?.domain_verified);
  const sysHost      = me?.system_tracking_host;
  // Priority: verified custom domain > instant subdomain on the shared base > app host
  const trackingBase = domainLive ? `https://${me.tracking_domain}` : (sysHost ? `https://${sysHost}` : BACKEND_URL);
  const snippet      = me?.site_key ? `<script src="${trackingBase}/api/page.js?k=${me.site_key}"></script>` : '';
  const domainSub    = (domain || 'track.yourdomain.com').replace(/^https?:\/\//, '').split('/')[0];

  return (
    <>
      <PageHeader title="Settings" description="The details that keep your reporting consistent." />
      <div className="settings-layout">
        <nav className="settings-nav" aria-label="Settings sections">
          {SECTIONS.map(x => <button key={x.id} type="button" className={section === x.id ? 'active' : ''} aria-pressed={section === x.id} onClick={() => setSection(x.id)}>{x.label}</button>)}
        </nav>
        <div>
      {error && <p role="alert" className="sp-form-error mb-4">{error}</p>}
      <div className="sp-settings-sections">
        <section className="form-section" hidden={section !== 'general'} aria-label="General settings"><h2>General settings</h2>

          <Row title="Workspace" desc="Your company name, shown across the dashboard.">
            <form aria-label="Workspace name" onSubmit={e => { e.preventDefault(); if (company.trim()) put({ name: company.trim() }, 'company'); }} className="sp-settings-inline flex items-center gap-2" style={{ maxWidth: 460 }}>
              <input
                data-testid="settings-company" aria-label="Workspace name" disabled={!!saving}
                value={company}
                onChange={e => { dirty.current.company = true; setCompany(e.target.value); }}
                className="flex-1 text-[13px] px-3 rounded-md outline-none"
                style={inputStyle}
              />
              <button
                type="submit" aria-label="Save workspace name"
                disabled={!company.trim() || !!saving} aria-busy={saving === 'company'}
                className="text-[12px] font-semibold px-3 rounded-md inline-flex items-center gap-1.5"
                style={btnPrimary}
              >
                {saving === 'company' && <Loader2 size={12} className="animate-spin" />} Save
              </button>
            </form>
          </Row>

          <Row title="Timezone" desc="All dates and chart buckets across the dashboard display in this timezone.">
            <div className="relative" style={{ maxWidth: 320 }}>
              <Clock size={13} className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: 'var(--text-dim)' }} />
              <select
                data-testid="settings-timezone" aria-label="Display timezone"
                value={timezone}
                onChange={e => { setTimezone(e.target.value); toast.success(`Timezone set to ${e.target.value}`); }}
                className="w-full appearance-none text-[13px] pl-9 pr-8 rounded-md outline-none cursor-pointer"
                style={{ ...inputStyle, height: 38 }}
              >
                {TIMEZONE_OPTIONS.map(o => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
          </Row>

          <Row title="Funnel type" desc="Tailors the dashboard to how you sell — webinar funnels get the Registrations page for platform webhooks.">
            <div className="flex items-center gap-1.5 flex-wrap">
              {FUNNEL_OPTIONS.map(o => {
                const active = me?.funnel_type === o.value;
                return (
                  <button
                    key={o.value}
                    data-testid={`settings-funnel-${o.value}`}
                    aria-pressed={active} disabled={!!saving || active} aria-busy={saving === 'funnel'}
                    onClick={() => put({ funnel_type: o.value }, 'funnel')}
                    className="text-[12px] px-2.5 rounded-md transition-colors"
                    style={{
                      height: 26,
                      backgroundColor: active ? 'var(--lime-tint)' : '#ffffff',
                      border: `1px solid ${active ? 'var(--lime-border)' : 'var(--stroke)'}`,
                      color: active ? 'var(--ink)' : 'var(--text-muted)',
                      fontWeight: active ? 600 : 450,
                    }}
                  >
                    {o.label}
                  </button>
                );
              })}
            </div>
          </Row>

        </section>
        <section className="form-section" hidden={section !== 'tracking'} aria-label="First-party tracking"><h2>First-party tracking</h2>
          {me?.tracking_base && (
            <Row
              title="Tracking subdomain"
              desc={`Your instant hostname on ${me.tracking_base} — live immediately, no DNS setup. Changing it breaks snippets installed with the old name. A verified custom domain below takes priority.`}
            >
              <form aria-label="Tracking subdomain" onSubmit={e => { e.preventDefault(); saveSubdomain(); }} className="sp-settings-inline flex items-center gap-2" style={{ maxWidth: 560 }}>
                <input
                  data-testid="settings-subdomain" aria-label="Tracking subdomain" disabled={!!saving}
                  value={subLabel}
                  onChange={e => { dirty.current.subdomain = true; setSubLabel(e.target.value); }}
                  placeholder="john"
                  className="flex-1 text-[13px] px-3 rounded-md outline-none"
                  style={{ ...inputStyle, fontFamily: 'var(--font-mono)' }}
                />
                <span className="text-[13px]" style={{ color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>
                  .{me.tracking_base}
                </span>
                <button
                  type="submit" aria-label="Save tracking subdomain"
                  disabled={!!saving || !subLabel.trim() || subLabel.trim() === me?.tracking_subdomain} aria-busy={saving === 'subdomain'}
                  className="text-[12px] font-semibold px-3 rounded-md inline-flex items-center gap-1.5"
                  style={btnPrimary}
                >
                  {saving === 'subdomain' && <Loader2 size={12} className="animate-spin" />} Save
                </button>
              </form>
              {sysHost && (
                <p className="text-[11px] mt-2" style={{ color: 'var(--text-muted)' }}>
                  Live now: <span style={{ fontFamily: 'var(--font-mono)' }}>https://{sysHost}</span>
                </p>
              )}
            </Row>
          )}

          <Row
            title="Tracking domain"
            desc="Serve the tracker and receive beacons on your own domain — first-party in every browser. URLs only switch over once the domain verifies, so tracking never silently breaks."
          >
            <form aria-label="Tracking domain" onSubmit={e => { e.preventDefault(); saveDomain(); }} className="sp-settings-inline flex items-center gap-2 mb-2.5" style={{ maxWidth: 560 }}>
              <input
                data-testid="settings-domain" aria-label="Tracking domain" disabled={!!saving}
                value={domain}
                onChange={e => { dirty.current.domain = true; setDomain(e.target.value); }}
                placeholder="track.yourdomain.com"
                className="flex-1 text-[13px] px-3 rounded-md outline-none"
                style={{ ...inputStyle, fontFamily: 'var(--font-mono)' }}
              />
              <button
                type="submit" aria-label="Save tracking domain"
                disabled={!!saving || domain.trim() === (me?.tracking_domain || '')} aria-busy={saving === 'domain'}
                className="text-[12px] font-semibold px-3 rounded-md inline-flex items-center gap-1.5"
                style={btnPrimary}
              >
                {saving === 'domain' && <Loader2 size={12} className="animate-spin" />} Save
              </button>
              {me?.tracking_domain && (
                <>
                  <button
                    data-testid="settings-verify-domain"
                    type="button" onClick={verifyDomain}
                    disabled={!!saving || domain.trim() !== me?.tracking_domain} aria-busy={saving === 'verify'} title={domain.trim() !== me?.tracking_domain ? 'Save the domain before verifying it' : 'Verify the saved tracking domain'}
                    className="text-[12px] font-semibold px-3 rounded-md inline-flex items-center gap-1.5"
                    style={{ height: 38, backgroundColor: 'var(--ink)', color: '#ffffff' }}
                  >
                    {saving === 'verify' ? <Loader2 size={12} className="animate-spin" /> : <Globe size={12} />} Verify
                  </button>
                  <button
                    type="button" onClick={clearDomain} disabled={!!saving} aria-busy={saving === 'domain'}
                    className="text-[12px] px-2 rounded-md"
                    style={btnGhost}
                  >
                    Clear
                  </button>
                </>
              )}
            </form>

            <div className="sp-settings-inline flex items-center gap-2 mb-3">
              <VerifyBadge verified={me?.domain_verified} domain={me?.tracking_domain} />
              {me?.tracking_domain && !me?.domain_verified && (
                <span className="text-[11px]" style={{ color: 'var(--text-muted)' }}>
                  Script and webhooks keep using {sysHost || (BACKEND_URL ? new URL(BACKEND_URL).host : 'the default domain')} until this verifies.
                </span>
              )}
            </div>

            {verifyMsg && !verifyMsg.verified && (
              <div className="mb-3 rounded-md px-3 py-2.5 text-[11px]"
                style={{ backgroundColor: '#faf4e7', border: '1px solid #eee0ba', color: '#a27a2c' }}>
                <b>{verifyMsg.status?.replace('_', ' ')}</b> — {verifyMsg.detail}
              </div>
            )}

            <div className="rounded-md p-3.5" style={{ backgroundColor: 'var(--lime-tint)', border: '1px solid var(--lime-border)', maxWidth: 560 }}>
              <p className="text-[11px] font-semibold mb-2" style={{ color: 'var(--ink)' }}>DNS record</p>
              <div className="grid gap-1 text-[11px]" style={{ gridTemplateColumns: '70px 1fr 1.4fr', fontFamily: 'var(--font-mono)' }}>
                <span style={{ color: 'var(--text-muted)' }}>Type</span>
                <span style={{ color: 'var(--text-muted)' }}>Host</span>
                <span style={{ color: 'var(--text-muted)' }}>Value</span>
                <span className="font-semibold" style={{ color: 'var(--ink)' }}>CNAME</span>
                <span className="font-semibold break-all" style={{ color: 'var(--ink)' }}>{domainSub.split('.')[0]}</span>
                <span className="font-semibold break-all" style={{ color: 'var(--ink)' }}>{cnameTarget}</span>
              </div>
              <p className="text-[10px] mt-2" style={{ color: 'var(--text-muted)' }}>
                An A record to the same server also works and avoids Safari's 7-day cookie cap on CNAME'd hosts.
              </p>
            </div>
          </Row>

          <Row title="Install" desc="Add to the <head> of every page in your funnel. The snippet always points at whichever domain is currently live.">
            <div style={{ maxWidth: 640 }}>
              <MonoRow value={snippet} label="Script" />
            </div>
          </Row>

          <Row title="Tag rules" desc="Tag contacts automatically: whenever a tracked page URL contains the text you enter, the visitor gets that tag — e.g. domain.com/training → new-funnel. Scheme, www and query string are ignored.">
            <div style={{ maxWidth: 640 }}>
              <form aria-label="Add tag rule" onSubmit={e => { e.preventDefault(); addRule(); }} className="sp-settings-inline flex items-center gap-2 mb-3">
                <input
                  data-testid="settings-rule-pattern" aria-label="Page URL pattern" disabled={!!saving || rulesUnavailable}
                  value={rulePattern}
                  onChange={e => setRulePattern(e.target.value)}
                  placeholder="domain.com/training"
                  className="flex-1 min-w-0 text-[13px] px-3 rounded-md outline-none"
                  style={inputStyle}
                />
                <input
                  data-testid="settings-rule-tag" aria-label="Tag name" disabled={!!saving || rulesUnavailable}
                  list="settings-known-tags"
                  value={ruleTag}
                  onChange={e => setRuleTag(e.target.value)}
                  placeholder="new-funnel"
                  className="text-[13px] px-3 rounded-md outline-none"
                  style={{ ...inputStyle, width: 170 }}
                />
                <datalist id="settings-known-tags">
                  {knownTags.map(t => <option key={t} value={t} />)}
                </datalist>
                <button
                  data-testid="settings-rule-add"
                  type="submit"
                  disabled={!!saving || rulesUnavailable} aria-busy={saving === 'rules'}
                  className="text-[12px] font-semibold px-3 rounded-md inline-flex items-center gap-1.5 shrink-0"
                  style={btnPrimary}
                >
                  {saving === 'rules' ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />}
                  Add rule
                </button>
              </form>
              <div className="rounded-md" style={{ border: '1px solid var(--stroke-soft)' }}>
                {ruleQuery.isError ? (
                  <div role="alert" className="sp-query-error"><p>Could not load tag rules. Existing rules have not been changed.</p><button type="button" className="btn" disabled={ruleQuery.isFetching} onClick={() => ruleQuery.refetch()}>Try again</button></div>
                ) : ruleQuery.isLoading || ruleQuery.isPending ? <p role="status" className="empty-state">Loading tag rules…</p> : rules.length === 0 ? (
                  <p className="px-3 py-4 text-[12px] text-center" style={{ color: 'var(--text-dim)' }}>No rules yet.</p>
                ) : rules.map((r, i) => (
                  <div key={r.id} className="flex items-center gap-3 px-3 py-2" data-testid={`settings-rule-${r.id}`}
                    style={{ borderTop: i > 0 ? '1px solid var(--stroke-soft)' : 'none' }}>
                    <code className="text-[12px] flex-1 min-w-0 truncate" style={{ color: 'var(--ink)' }}>{r.pattern}</code>
                    <span className="text-[11px] shrink-0" style={{ color: 'var(--text-dim)' }}>tags as</span>
                    <span className="inline-flex items-center text-[11px] px-1.5 rounded shrink-0"
                      style={{ height: 20, fontWeight: 500, backgroundColor: 'var(--surface-2)', color: 'var(--text-muted)', border: '1px solid var(--stroke)' }}>
                      {r.tag}
                    </span>
                    <button type="button" aria-label={`Remove tag rule ${r.pattern} → ${r.tag}`} onClick={() => removeRule(r)} title="Remove" disabled={!!saving || rulesUnavailable} aria-busy={saving === 'rules'}
                      className="shrink-0 p-1.5 rounded-md hover:bg-[#FDF2F2]" style={{ color: 'var(--text-dim)' }}>
                      <Trash2 size={12} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </Row>

          {me?.funnel_type === 'webinar' && (
            <Row title="Registration tags" desc="Analytics counts a lead as registered when they carry any of these tags. Leave empty for the defaults: stealth (registration webhook) and registered (thank-you page / tag rules).">
              <form aria-label="Registration tags" onSubmit={e => { e.preventDefault(); put({ registration_tags: regTags.split(',').map(t => t.trim()).filter(Boolean) }, 'regtags'); }} className="sp-settings-inline flex items-center gap-2" style={{ maxWidth: 640 }}>
                <input
                  data-testid="settings-registration-tags" aria-label="Registration tags" disabled={!!saving}
                  value={regTags}
                  onChange={e => { dirty.current.regtags = true; setRegTags(e.target.value); }}
                  placeholder="stealth, registered"
                  className="flex-1 min-w-0 text-[13px] px-3 rounded-md outline-none"
                  style={inputStyle}
                />
                <button
                  type="submit" aria-label="Save registration tags"
                  disabled={!!saving} aria-busy={saving === 'regtags'}
                  className="text-[12px] font-semibold px-3 rounded-md inline-flex items-center gap-1.5 shrink-0"
                  style={btnPrimary}
                >
                  {saving === 'regtags' ? <Loader2 size={12} className="animate-spin" /> : null}
                  Save
                </button>
              </form>
            </Row>
          )}

        </section>
        <section className="form-section" hidden={section !== 'developer'} aria-label="API credentials"><h2>API credentials</h2>
          <SurfaceHead title="API credentials" />
          <Row title="Keys" desc="The site key is public (it ships inside the script). The API key is secret — it authenticates webhooks.">
            <div className="space-y-3" style={{ maxWidth: 640 }}>
              <div>
                <p className="text-[11px] font-medium mb-1" style={{ color: 'var(--text-muted)' }}>Site key</p>
                <MonoRow value={me?.site_key || ''} label="Site key" />
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <p className="text-[11px] font-medium" style={{ color: 'var(--text-muted)' }}>API key</p>
                  <button
                    data-testid="settings-rotate-key"
                    onClick={rotateKey}
                    disabled={!!saving} aria-busy={saving === 'key'}
                    className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded"
                    style={{ color: 'var(--brand-red)' }}
                  >
                    {saving === 'key' ? <Loader2 size={10} className="animate-spin" /> : <RefreshCw size={10} />}
                    Rotate
                  </button>
                </div>
                <MonoRow value={me?.api_key || ''} label="API key" masked />
              </div>
            </div>
          </Row>

        </section>
        <section className="form-section" hidden={section !== 'team'} aria-label="Workspace members"><h2>Workspace members</h2>
          <Row title="Team" desc="Invite teammates to this workspace. They get a welcome email with a link to set their password.">
            <div style={{ maxWidth: 640 }}>
              <form aria-label="Invite teammate" onSubmit={e => { e.preventDefault(); if (e.currentTarget.reportValidity()) sendInvite(); }} className="sp-settings-inline flex items-center gap-2 mb-3">
                <input
                  data-testid="settings-invite-email" aria-label="Teammate email" disabled={!!saving} type="email" required autoComplete="email"
                  value={inviteEmail}
                  onChange={e => setInviteEmail(e.target.value)}
                  placeholder="teammate@company.com"
                  className="flex-1 text-[13px] px-3 rounded-md outline-none"
                  style={inputStyle}
                />
                <button
                  data-testid="settings-invite-send"
                  type="submit"
                  disabled={!!saving || !inviteEmail.trim()} aria-busy={saving === 'invite'}
                  className="text-[12px] font-semibold px-3 rounded-md inline-flex items-center gap-1.5"
                  style={btnPrimary}
                >
                  {saving === 'invite' ? <Loader2 size={12} className="animate-spin" /> : <UserPlus size={12} />}
                  Invite
                </button>
              </form>

              {inviteLink && (
                <div className="mb-3 rounded-md px-3 py-2.5"
                  style={{ backgroundColor: 'var(--lime-tint)', border: '1px solid var(--lime-border)' }}>
                  <p className="text-[11px] font-semibold mb-1.5 flex items-center gap-1" style={{ color: 'var(--ink)' }}>
                    <Link2 size={11} /> Share this invite link
                  </p>
                  <MonoRow value={inviteLink} label="Invite link" />
                </div>
              )}

              <div className="rounded-md" style={{ border: '1px solid var(--stroke-soft)' }}>
                {teamQuery.isError ? (
                  <div role="alert" className="sp-query-error"><p>Could not load team members.</p><button type="button" className="btn" disabled={teamQuery.isFetching} onClick={() => teamQuery.refetch()}>Try again</button></div>
                ) : teamQuery.isLoading || teamQuery.isPending ? <p role="status" className="empty-state">Loading team members…</p> : team.length === 0 ? (
                  <p className="px-3 py-4 text-[12px] text-center" style={{ color: 'var(--text-dim)' }}>No team members yet.</p>
                ) : team.map((u, i) => (
                  <div key={u.id} className="flex items-center gap-3 px-3 py-2"
                    style={{ borderTop: i > 0 ? '1px solid var(--stroke-soft)' : 'none' }}>
                    <span className="w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0"
                      style={{ backgroundColor: u.role === 'owner' ? 'var(--lime-soft)' : 'var(--surface-2)', color: 'var(--ink)', border: '1px solid var(--stroke-soft)' }}>
                      {(u.name || u.email).charAt(0).toUpperCase()}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block text-[13px] font-medium truncate" style={{ color: 'var(--ink)' }}>
                        {u.name || u.email}
                      </span>
                      {u.name && <span className="block text-[11px] truncate" style={{ color: 'var(--text-dim)' }}>{u.email}</span>}
                    </span>
                    <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded uppercase shrink-0"
                      style={{ backgroundColor: 'rgba(32,33,31,0.06)', color: 'var(--text-muted)', letterSpacing: '0.04em' }}>
                      {u.role}
                    </span>
                    {u.status === 'invited' && (
                      <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded shrink-0"
                        style={{ backgroundColor: '#faf4e7', color: '#a27a2c', border: '1px solid #eee0ba' }}>
                        invited
                      </span>
                    )}
                    {u.role !== 'owner' && (
                      <button onClick={() => removeMember(u)} title="Remove" disabled={!!saving} aria-busy={saving === `member-${u.id}`} aria-label={`Remove ${u.email} from workspace`}
                        className="shrink-0 p-1.5 rounded-md hover:bg-[#FDF2F2]" style={{ color: 'var(--text-dim)' }}>
                        <Trash2 size={12} />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </Row>

        </section>
        <section className="form-section" hidden={section !== 'developer'} aria-label="Webhook endpoints"><h2>Webhook endpoints</h2>
          <SurfaceHead title="Integration endpoints" />
          <Row title="Webhook endpoints" desc="Point your platforms here — authenticated with your API key.">
            <div className="space-y-2.5" style={{ maxWidth: 640 }}>
              <WebhookEndpoint
                label="Conversions"
                desc="purchases from Stripe, Whop, GHL, Zapier…"
                url={`${trackingBase}/api/sales/webhook?key=${me?.api_key || ''}`}
              />
              {me?.funnel_type === 'webinar' && (
                <WebhookEndpoint
                  label="Registrations"
                  desc="webinar platforms like StealthWebinar"
                  url={`${trackingBase}/api/stealth/webhook?key=${me?.api_key || ''}`}
                />
              )}
            </div>
          </Row>

        </section>
        <section className="form-section" hidden={section !== 'billing'} aria-label="Subscription"><h2>Subscription</h2>
          <Row title="Subscription" desc="Cancelling stops tracking for every domain on this workspace — we confirm personally before anything changes.">
            <div style={{ maxWidth: 640 }}>
              {cancelDate ? (
                <div className="rounded-md px-3.5 py-3 flex items-start gap-2.5"
                  style={{ backgroundColor: 'var(--lime-tint)', border: '1px solid var(--lime-border)' }}>
                  <CheckCircle2 size={14} className="shrink-0 mt-0.5" style={{ color: 'var(--brand-red)' }} />
                  <div>
                    <p className="text-[13px] font-semibold" style={{ color: 'var(--ink)' }}>
                      Your cancellation request has been made
                    </p>
                    <p className="text-[12px] mt-0.5" style={{ color: 'var(--text-muted)' }}>
                      Someone will reach out to you shortly to confirm. Tracking keeps running until then.
                      {cancelDate ? ` Requested ${new Date(cancelDate).toLocaleDateString()}.` : ''}
                    </p>
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-3">
                  <button
                    data-testid="settings-cancel-subscription"
                    onClick={requestCancel}
                    disabled={!!saving} aria-busy={saving === 'cancel'}
                    className="inline-flex items-center gap-1.5 text-[12px] font-semibold px-3 rounded-md"
                    style={{ height: 36, border: '1px solid var(--stroke)', color: 'var(--red-error)', backgroundColor: '#ffffff', opacity: saving === 'cancel' ? 0.7 : 1 }}
                  >
                    {saving === 'cancel' ? <Loader2 size={12} className="animate-spin" /> : <AlertTriangle size={12} />}
                    Cancel subscription
                  </button>
                  <span className="text-[12px]" style={{ color: 'var(--text-dim)' }}>
                    Sends a request — nothing changes until we confirm with you.
                  </span>
                </div>
              )}
            </div>
          </Row>

        </section>
      </div>
        </div>
      </div>
    </>
  );
}
